import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './About.css';

const About: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const pastors = {
    barmond: {
      name: 'Apostle Brian A. Harris',
      title: 'Founder & Lead Pastor',
      image: '/images/pastor_pic.jpg',
      alt: 'Apostle Brian A. Harris - Founder & Lead Pastor',
      scripturePreview: '"Go ye therefore, and teach all nations, baptizing them in the name of the Father, and of the Son, and of the Holy Ghost..."',
      scriptureRef: 'Matthew 28:19-20 (KJV)',
      id: 'barmond'
    },
    kimberly: {
      name: 'Dr. Kimberly R. Harris',
      title: 'Co-founder & Executive Pastor',
      image: '/images/dr_kimberly_harris.PNG',
      alt: 'Dr. Kimberly R. Harris - Co-founder & Executive Pastor',
      scripturePreview: '"We are therefore Christ\'s ambassadors, as though God were making his appeal through us..."',
      scriptureRef: '2 Corinthians 5:20 (KJV)',
      id: 'kimberly'
    }
  };

  return (
    <div className="about-page">
      <div className="about-hero">
        <motion.div 
          className="about-hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Our Culture, Mission, and Beliefs</h1>
          <p className="hero-subtitle">Get to know who we are and what drives us</p>
        </motion.div>
      </div>

      <div className="about-container">
        {/* Mission Section */}
        <motion.section 
          className="about-section"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="section-header">
            <motion.h2 variants={fadeInUp}>Our Mission</motion.h2>
            <motion.p className="mission-tagline" variants={fadeInUp}>Trust | Love | Understand</motion.p>
          </div>
          <div className="section-content">
            <motion.p className="mission-statement" variants={fadeInUp}>
              To build relationships of trust, share the love of God, and understand generations, 
              that all may be enriched and developed as disciples of Christ.
            </motion.p>
            <motion.blockquote className="scripture-quote" variants={fadeInUp}>
              "But ye are a chosen generations, a royal priesthood, an holy nation, a peculiar people; 
              that ye should shew forth the praises of him who hath called you out of darkness into his marvelous light."
              <cite>— I Peter 2:9</cite>
            </motion.blockquote>
          </div>
        </motion.section>

        {/* Meet Our Pastors Section */}
        <motion.section 
          className="about-section pastors-section"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="section-header">
            <motion.h2 variants={fadeInUp}>Meet Our Pastors</motion.h2>
            <motion.p className="section-intro" variants={fadeInUp}>
              Led by a passionate team dedicated to serving God and loving people
            </motion.p>
          </div>
          <div className="pastors-grid">
            <Link to={`/pastor/${pastors.barmond.id}`} className="pastor-card-link">
              <motion.div 
                className="pastor-card" 
                variants={fadeInUp}
              >
                <div className="pastor-image-container">
                  <img 
                    src={pastors.barmond.image} 
                    alt={pastors.barmond.alt} 
                    className="pastor-image"
                  />
                </div>
                <div className="pastor-info">
                  <h3>{pastors.barmond.name}</h3>
                  <p className="pastor-title">{pastors.barmond.title}</p>
                  <blockquote className="pastor-scripture-preview">
                    {pastors.barmond.scripturePreview}
                    <cite>— {pastors.barmond.scriptureRef}</cite>
                  </blockquote>
                  <p className="pastor-click-hint">Click to learn more</p>
                </div>
              </motion.div>
            </Link>
            <Link to={`/pastor/${pastors.kimberly.id}`} className="pastor-card-link">
              <motion.div 
                className="pastor-card" 
                variants={fadeInUp}
              >
                <div className="pastor-image-container">
                  <img 
                    src={pastors.kimberly.image} 
                    alt={pastors.kimberly.alt} 
                    className="pastor-image"
                  />
                </div>
                <div className="pastor-info">
                  <h3>{pastors.kimberly.name}</h3>
                  <p className="pastor-title">{pastors.kimberly.title}</p>
                  <blockquote className="pastor-scripture-preview">
                    {pastors.kimberly.scripturePreview}
                    <cite>— {pastors.kimberly.scriptureRef}</cite>
                  </blockquote>
                  <p className="pastor-click-hint">Click to learn more</p>
                </div>
              </motion.div>
            </Link>
          </div>
        </motion.section>

        {/* Culture Section */}
        <motion.section 
          className="about-section culture-section"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="section-header">
            <motion.h2 variants={fadeInUp}>Our Culture</motion.h2>
          </div>
          <div className="section-content">
            <motion.p className="culture-description" variants={fadeInUp}>
              A Non-denominational, Multicultural, Bible-teaching, Christ-centered, Spirit-filled, 
              Mission-driven church designed for all people.
            </motion.p>
            <motion.p variants={fadeInUp}>
              It is our culture at Gener8ions Church to neither judge nor condemn, but to provide a healthy, 
              positive, life-giving atmosphere that expresses the unconditional Love and Grace of God. 
              We value souls and are true advocates for mentorship, discipleship and the development of a 
              personal relationship with God for each individual. We celebrate diversity and embrace simplicity. 
              We honor God's presence and inspire others through servant leadership driven by a spirit of excellence.
            </motion.p>
          </div>
        </motion.section>

        {/* Values Grid */}
        <motion.section 
          className="values-section"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeInUp}>What We Value</motion.h2>
          <div className="values-grid">
            <motion.div className="value-card" variants={fadeInUp}>
              <div className="value-icon">💝</div>
              <h3>Unconditional Love</h3>
              <p>Expressing the love and grace of God to all people</p>
            </motion.div>
            <motion.div className="value-card" variants={fadeInUp}>
              <div className="value-icon">📚</div>
              <h3>Mentorship & Discipleship</h3>
              <p>Investing in personal growth and spiritual development</p>
            </motion.div>
            <motion.div className="value-card" variants={fadeInUp}>
              <div className="value-icon">🌍</div>
              <h3>Multicultural Community</h3>
              <p>Celebrating the beauty of our diverse community</p>
            </motion.div>
            <motion.div className="value-card" variants={fadeInUp}>
              <div className="value-icon">🙏</div>
              <h3>God's Presence</h3>
              <p>Honoring and seeking God in everything we do</p>
            </motion.div>
            <motion.div className="value-card" variants={fadeInUp}>
              <div className="value-icon">⭐</div>
              <h3>Excellence</h3>
              <p>Pursuing excellence in servant leadership</p>
            </motion.div>
            <motion.div className="value-card" variants={fadeInUp}>
              <div className="value-icon">🤝</div>
              <h3>Relationships</h3>
              <p>Building authentic connections based on trust</p>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default About;

